import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft, ArrowRight, ExternalLink, TrendingUp, Code2,
  Quote, CheckCircle2, Loader2
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/config/routes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Translations
const translations = {
  nl: {
    back: 'Terug naar portfolio',
    challenge: 'De uitdaging',
    solution: 'Onze oplossing',
    results: 'Behaalde resultaten',
    technologies: 'Gebruikte technologieën',
    testimonial: 'Wat de klant zegt',
    visitWebsite: 'Bezoek website',
    gallery: 'Projectgalerij',
    ctaTitle: 'Wil jij vergelijkbare resultaten?',
    ctaSubtitle: 'Neem contact op voor een vrijblijvend gesprek over de mogelijkheden.',
    ctaButton: 'Vraag gratis advies',
    notFound: 'Case study niet gevonden',
    notFoundText: 'Deze case study bestaat niet of is verwijderd.',
    backToPortfolio: 'Terug naar portfolio',
    loading: 'Laden...'
  },
  en: {
    back: 'Back to portfolio',
    challenge: 'The challenge',
    solution: 'Our solution',
    results: 'Achieved results',
    technologies: 'Technologies used',
    testimonial: 'What the client says',
    visitWebsite: 'Visit website',
    gallery: 'Project gallery',
    ctaTitle: 'Want similar results?',
    ctaSubtitle: 'Contact us for a free consultation about the possibilities.',
    ctaButton: 'Request free advice',
    notFound: 'Case study not found',
    notFoundText: 'This case study does not exist or has been removed.',
    backToPortfolio: 'Back to portfolio',
    loading: 'Loading...'
  },
  fr: {
    back: 'Retour au portfolio',
    challenge: 'Le défi',
    solution: 'Notre solution',
    results: 'Résultats obtenus',
    technologies: 'Technologies utilisées',
    testimonial: 'Ce que dit le client',
    visitWebsite: 'Visiter le site',
    gallery: 'Galerie du projet',
    ctaTitle: 'Vous voulez des résultats similaires?',
    ctaSubtitle: 'Contactez-nous pour une consultation gratuite.',
    ctaButton: 'Demander un conseil gratuit',
    notFound: 'Étude de cas introuvable',
    notFoundText: 'Cette étude de cas n\'existe pas.',
    backToPortfolio: 'Retour au portfolio',
    loading: 'Chargement...'
  },
  de: {
    back: 'Zurück zum Portfolio',
    challenge: 'Die Herausforderung',
    solution: 'Unsere Lösung',
    results: 'Erzielte Ergebnisse',
    technologies: 'Verwendete Technologien',
    testimonial: 'Was der Kunde sagt',
    visitWebsite: 'Website besuchen',
    gallery: 'Projektgalerie',
    ctaTitle: 'Möchten Sie ähnliche Ergebnisse?',
    ctaSubtitle: 'Kontaktieren Sie uns für ein kostenloses Beratungsgespräch.',
    ctaButton: 'Kostenlose Beratung anfordern',
    notFound: 'Fallstudie nicht gefunden',
    notFoundText: 'Diese Fallstudie existiert nicht.',
    backToPortfolio: 'Zurück zum Portfolio',
    loading: 'Laden...'
  }
};

// Color mapping for sectors
const sectorColors = {
  'horeca': 'from-amber-500 to-orange-600',
  'bouw': 'from-slate-600 to-slate-800',
  'coaching': 'from-purple-500 to-pink-600',
  'retail': 'from-rose-500 to-red-600',
  'diensten': 'from-emerald-500 to-teal-600'
};

const CaseStudyPage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [activeImage, setActiveImage] = useState(0);
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const t = translations[language] || translations.nl;

  // Fetch case study from API
  useEffect(() => {
    const fetchCaseStudy = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/api/casestudies/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('not_found');
          } else {
            setError('fetch_error');
          }
          return;
        }
        const data = await response.json();
        setCaseStudy(data);
      } catch (err) {
        console.error('Error fetching case study:', err);
        setError('fetch_error');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCaseStudy();
    }
  }, [slug]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-32 pb-20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#2563EB] animate-spin mx-auto mb-4" />
          <p className="text-slate-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (error || !caseStudy) {
    return (
      <div className="min-h-screen bg-slate-50 pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">{t.notFound}</h1>
          <p className="text-slate-600 mb-8">{t.notFoundText}</p>
          <Link
            to={getLocalizedPath('portfolio', language)}
            className="inline-flex items-center gap-2 bg-[#2563EB] text-white font-semibold py-3 px-6 rounded-full hover:bg-[#1D4ED8] transition-colors"
            data-testid="back-to-portfolio-link"
          >
            <ArrowLeft size={18} />
            {t.backToPortfolio}
          </Link>
        </div>
      </div>
    );
  }

  // Get color based on sector or use default
  const color = caseStudy.color || sectorColors[caseStudy.sector] || 'from-blue-500 to-indigo-600';
  const allImages = [caseStudy.image_hero, ...(caseStudy.image_gallery || [])];

  return (
    <>
      <Helmet>
        <title>{`${caseStudy.name} | Netblitz Case Study`}</title>
        <meta name="description" content={caseStudy.description?.[language] || caseStudy.description?.nl} />
      </Helmet>

      <div className="min-h-screen bg-white" data-testid="case-study-page">
        {/* Hero Section */}
        <section className="relative pt-24 sm:pt-32 pb-0 overflow-hidden">
          {/* Background gradient */}
          <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-5`} />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
            {/* Back link */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-6 sm:mb-8"
            >
              <Link
                to={getLocalizedPath('portfolio', language)}
                className="inline-flex items-center gap-2 text-slate-600 hover:text-[#2563EB] transition-colors text-sm sm:text-base"
                data-testid="back-link"
              >
                <ArrowLeft size={18} />
                {t.back}
              </Link>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Text Content */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="order-2 lg:order-1"
              >
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r ${color} text-white mb-4`}>
                  {caseStudy.sector.charAt(0).toUpperCase() + caseStudy.sector.slice(1)}
                </span>
                
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 leading-tight" data-testid="case-study-title">
                  {caseStudy.name}
                </h1>
                
                <p className="text-xl sm:text-2xl text-slate-600 mb-6">
                  {caseStudy.tagline?.[language] || caseStudy.tagline?.nl}
                </p>
                
                <p className="text-base sm:text-lg text-slate-500 leading-relaxed mb-8">
                  {caseStudy.description?.[language] || caseStudy.description?.nl}
                </p>

                {caseStudy.website_url && (
                  <a
                    href={caseStudy.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#2563EB] text-white font-semibold py-3.5 px-6 rounded-full hover:bg-[#1D4ED8] transition-all shadow-lg hover:shadow-xl min-h-[48px]"
                    data-testid="visit-website-link"
                  >
                    {t.visitWebsite}
                    <ExternalLink size={18} />
                  </a>
                )}
              </motion.div>

              {/* Hero Image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="order-1 lg:order-2"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={caseStudy.image_hero}
                    alt={`${caseStudy.name} - Project hero image`}
                    decoding="async"
                    className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${color} opacity-20`} />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-12 sm:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10 sm:mb-12"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                {t.results}
              </h2>
            </motion.div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="results-grid">
              {caseStudy.results?.map((result, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center"
                  data-testid={`result-card-${index}`}
                >
                  <div className={`text-3xl sm:text-4xl font-bold bg-gradient-to-r ${color} bg-clip-text text-transparent mb-2`}>
                    {result.metric}
                  </div>
                  <div className="text-sm sm:text-base text-slate-600">
                    {result.label?.[language] || result.label?.nl}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Challenge & Solution */}
        <section className="py-12 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid md:grid-cols-2 gap-8 sm:gap-12">
              {/* Challenge */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                data-testid="challenge-section"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900">{t.challenge}</h3>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  {caseStudy.challenge?.[language] || caseStudy.challenge?.nl}
                </p>
              </motion.div>

              {/* Solution */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                data-testid="solution-section"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900">{t.solution}</h3>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  {caseStudy.solution?.[language] || caseStudy.solution?.nl}
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="py-12 sm:py-16 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="flex items-center justify-center gap-2 mb-6">
                <Code2 className="w-5 h-5 text-blue-400" />
                <h3 className="text-lg sm:text-xl font-semibold text-white">{t.technologies}</h3>
              </div>
              <div className="flex flex-wrap justify-center gap-3" data-testid="technologies-list">
                {caseStudy.technologies?.map((tech, index) => (
                  <motion.span
                    key={tech}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="px-4 py-2 bg-slate-800 text-slate-300 rounded-full text-sm font-medium"
                  >
                    {tech}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Gallery */}
        {caseStudy.image_gallery && caseStudy.image_gallery.length > 0 && (
          <section className="py-12 sm:py-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-xl sm:text-2xl font-bold text-slate-900 text-center mb-8"
              >
                {t.gallery}
              </motion.h3>

              <div className="relative">
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  className="rounded-2xl overflow-hidden shadow-xl"
                >
                  <img
                    src={allImages[activeImage]}
                    alt={`${caseStudy.name} - Gallery image ${activeImage + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-64 sm:h-96 object-cover"
                  />
                </motion.div>

                {/* Navigation */}
                <div className="flex justify-center gap-2 mt-4">
                  {allImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveImage(index)}
                      className={`w-3 h-3 rounded-full transition-all ${
                        activeImage === index 
                          ? 'bg-[#2563EB] scale-110' 
                          : 'bg-slate-300 hover:bg-slate-400'
                      }`}
                      aria-label={`View image ${index + 1}`}
                      data-testid={`gallery-dot-${index}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Testimonial */}
        {caseStudy.testimonial_quote && (
          <section className="py-12 sm:py-20 bg-slate-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-8 sm:p-12 shadow-lg border border-slate-100 relative"
                data-testid="testimonial-section"
              >
                <Quote className="absolute top-6 left-6 w-12 h-12 text-slate-100" />
                
                <div className="relative">
                  <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-6">
                    {t.testimonial}
                  </h3>
                  
                  <blockquote className="text-lg sm:text-xl text-slate-700 italic leading-relaxed mb-6">
                    "{caseStudy.testimonial_quote?.[language] || caseStudy.testimonial_quote?.nl}"
                  </blockquote>
                  
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${color} flex items-center justify-center text-white font-bold text-lg`}>
                      {caseStudy.testimonial_author?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{caseStudy.testimonial_author}</p>
                      <p className="text-sm text-slate-500">{caseStudy.testimonial_role}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className={`py-16 sm:py-24 bg-gradient-to-br ${color}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4">
                {t.ctaTitle}
              </h2>
              <p className="text-lg sm:text-xl text-white/80 mb-8">
                {t.ctaSubtitle}
              </p>
              <Link
                to={getLocalizedPath('contact', language)}
                className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold py-4 px-8 rounded-full hover:bg-slate-100 transition-all shadow-lg min-h-[52px]"
                data-testid="cta-contact-link"
              >
                {t.ctaButton}
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CaseStudyPage;
